import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

const EASE = [0.16, 1, 0.3, 1];

const LandingChoice = () => {
  const navigate = useNavigate();

  const go = (path) => {
    navigate(path);
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  return (
    <div className="min-h-screen bg-[#040F2D] font-institutional-body antialiased relative overflow-hidden">
      {/* Ambient gold gradient orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-[#D4AF37]/8 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-0 -right-32 w-[600px] h-[600px] bg-[#D4AF37]/6 rounded-full blur-[140px]"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-6 flex items-center justify-end">
        <div className="flex items-center gap-8 text-white/50 text-xs uppercase tracking-[0.2em] font-institutional-body">
          <span className="hidden md:inline">Uganda</span>
          <span className="hidden md:inline w-6 h-px bg-white/20"></span>
          <span className="hidden md:inline">Dubai</span>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 min-h-[calc(100vh-88px)] flex items-center px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left column - branding */}
            <div className="lg:col-span-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.05, ease: EASE }}
                className="mb-10 md:mb-12"
              >
                <motion.img
                  src="https://customer-assets.emergentagent.com/job_563d6243-6438-4f0a-a4a2-1ab5ee42a002/artifacts/gk86wjkd_Logo_Goldcif-removebg-preview.png"
                  alt="GOLDCIF"
                  className="h-56 md:h-72 lg:h-80 w-auto"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
                  style={{ filter: 'drop-shadow(0 20px 40px rgba(212, 175, 55, 0.15))' }}
                />
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                className="uppercase text-xs tracking-[0.24em] text-[#D4AF37] font-semibold font-institutional-body"
              >
                Legal Gold Trading &middot; Est. 2024
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.25, ease: EASE }}
                className="font-institutional-heading text-white text-5xl md:text-6xl lg:text-7xl font-semibold leading-[0.95] tracking-tight mt-6"
              >
                Africa&apos;s trusted gold trading infrastructure.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.4, ease: EASE }}
                className="text-white/60 text-lg md:text-xl mt-8 max-w-xl font-institutional-body font-light leading-relaxed"
              >
                GOLDCIF connects licensed artisanal gold production to international institutional markets — through a fully regulated corridor between Uganda and Dubai.
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
                className="mt-12 flex items-center gap-8 text-white/40 text-xs uppercase tracking-[0.2em] font-institutional-body"
              >
                <span>100% Licensed</span>
                <span className="w-6 h-px bg-white/20"></span>
                <span>OECD Aligned</span>
                <span className="w-6 h-px bg-white/20 hidden sm:inline-block"></span>
                <span className="hidden sm:inline">Verified Producers</span>
              </motion.div>
            </div>

            {/* Right column - choice cards */}
            <div className="lg:col-span-6 lg:pl-8">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
                className="uppercase text-xs tracking-[0.24em] text-[#D4AF37] font-semibold font-institutional-body mb-8"
              >
                Choose your profile
              </motion.p>

              <div className="space-y-px bg-white/5">
                {/* Seller card */}
                <motion.button
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
                  onClick={() => go('/sellers')}
                  data-testid="choose-seller-btn"
                  className="w-full text-left group bg-[#040F2D] hover:bg-[#0B132B] transition-colors p-8 md:p-10 border border-white/10 hover:border-[#D4AF37]/50"
                >
                  <div className="flex items-start justify-between mb-8">
                    <span className="font-institutional-data text-[#D4AF37]/70 text-xs tracking-widest">01</span>
                    <ArrowUpRight className="w-5 h-5 text-white/20 group-hover:text-[#D4AF37] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                  </div>
                  <p className="uppercase text-[10px] tracking-[0.24em] text-white/40 font-institutional-body">For Producers &amp; Mandates</p>
                  <h2 className="font-institutional-heading text-white text-3xl md:text-4xl font-semibold mt-3 tracking-tight leading-tight">
                    I am a Seller
                  </h2>
                  <p className="text-white/50 mt-4 text-sm md:text-base font-institutional-body font-light leading-relaxed">
                    Licensed and unlicensed artisanal miners seeking legal, transparent access to international gold markets.
                  </p>
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <span className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-institutional-body font-medium">
                      Access Seller Platform →
                    </span>
                  </div>
                </motion.button>

                {/* Investor card */}
                <motion.button
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.9, delay: 1, ease: EASE }}
                  onClick={() => go('/investors')}
                  data-testid="choose-investor-btn"
                  className="w-full text-left group bg-[#040F2D] hover:bg-[#0B132B] transition-colors p-8 md:p-10 border border-white/10 hover:border-[#D4AF37]/50"
                >
                  <div className="flex items-start justify-between mb-8">
                    <span className="font-institutional-data text-[#D4AF37]/70 text-xs tracking-widest">02</span>
                    <ArrowUpRight className="w-5 h-5 text-white/20 group-hover:text-[#D4AF37] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                  </div>
                  <p className="uppercase text-[10px] tracking-[0.24em] text-white/40 font-institutional-body">For Family Offices &amp; Offtakers</p>
                  <h2 className="font-institutional-heading text-white text-3xl md:text-4xl font-semibold mt-3 tracking-tight leading-tight">
                    I am an Investor
                  </h2>
                  <p className="text-white/50 mt-4 text-sm md:text-base font-institutional-body font-light leading-relaxed">
                    Institutional investors and offtakers seeking structured exposure to African gold through a regulated infrastructure platform.
                  </p>
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <span className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-institutional-body font-medium">
                      Access Investor Platform →
                    </span>
                  </div>
                </motion.button>
              </div>
            </div>
          </div>

          {/* Bottom footnote */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4"
          >
            <p className="text-white/40 text-xs font-institutional-body">
              © {new Date().getFullYear()} GOLDCIF. Legal gold export platform.
            </p>
            <p className="text-white/40 text-xs font-institutional-body">
              Regulated operations &middot; compliance@goldcif.com
            </p>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default LandingChoice;
